/* Reseller landing page — behaviour.
 *
 * Loaded only on the reseller template, deferred. Each section registers its
 * own behaviour here as later cards land: the profit calculator (sect-006) and
 * the registration form's WhatsApp hand-off (form-008).
 *
 * Nothing here may assume a section is present — the client can reorder or
 * remove any of them from the theme editor.
 */
(function () {
  'use strict';

  /* The sticky mobile CTA is position:fixed, so the page needs bottom padding
     to keep the footer clear of it. Applied from JS rather than CSS because the
     padding belongs on <body>, which is Origin's element, not ours — and only
     when the section is actually on the page. */
  function reserveSpaceForStickyCta() {
    var hasSticky = document.querySelector('.rsl-sticky') !== null;
    document.body.classList.toggle('rsl-has-sticky', hasSticky);
  }

  /* Profit calculator.
   *
   * Every figure comes from data attributes rendered by the section, never from
   * constants here — the client edits margins in the theme editor and this file
   * is not touched. Liquid renders the opening state server-side, so the numbers
   * are already correct before this runs; the script only handles changes.
   *
   * Formatting matches snippets/rsl-rupiah.liquid: Indonesian grouping with dot
   * separators. The two must agree, or the figures would shift the moment a
   * visitor clicks a pill. */
  function formatRupiah(value, prefix) {
    var rounded = Math.round(Number(value) || 0);
    var grouped = String(Math.abs(rounded)).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return (prefix || '') + (rounded < 0 ? '-' : '') + grouped;
  }

  function setUpCalculator(root) {
    var pills = Array.prototype.slice.call(root.querySelectorAll('[data-quantity]'));
    if (!pills.length) return;

    var price = Number(root.dataset.price) || 0;
    var weeks = Number(root.dataset.weeks) || 0;
    var prefix = root.dataset.prefix || '';
    var suffix = root.dataset.suffix || '';

    var out = {};
    Array.prototype.forEach.call(root.querySelectorAll('[data-rsl-out]'), function (el) {
      out[el.dataset.rslOut] = el;
    });

    function render(pill) {
      var quantity = Number(pill.dataset.quantity) || 0;
      var margin = Number(pill.dataset.margin) || 0;
      var weekly = margin * quantity;

      pills.forEach(function (p) {
        var on = p === pill;
        p.classList.toggle('rsl-pill--on', on);
        p.setAttribute('aria-pressed', on ? 'true' : 'false');
      });

      if (out.price) out.price.textContent = formatRupiah(price, prefix) + suffix;
      if (out.margin) out.margin.textContent = formatRupiah(margin, prefix);
      if (out.weekly) out.weekly.textContent = formatRupiah(weekly, prefix);
      if (out.monthly) out.monthly.textContent = formatRupiah(weekly * weeks, prefix);
    }

    pills.forEach(function (pill) {
      pill.addEventListener('click', function () {
        render(pill);
      });
    });
  }

  /* Registration → WhatsApp hand-off.
   *
   * The form posts natively, so the lead is already safe by the time any of this
   * runs. All this does is personalise the WhatsApp message afterwards: Shopify's
   * contact form redirects back with a success flag but not with the submitted
   * values, so the name and city are stashed just before submit and read once on
   * the way back.
   *
   * Everything degrades: no stash, no JavaScript, or a fresh session all still
   * produce a working link with the generic message. */
  var STASH_KEY = 'rsl:registrant';

  /* Indonesian numbers get typed every which way — 0811…, 62811…, +62 811…,
   * with spaces and dashes. They are normalised to one international form so
   * the `wa:` tag is consistent and the client can search for a number without
   * guessing how the reseller happened to type it. */
  function toInternational(raw) {
    var digits = String(raw || '').replace(/[^\d+]/g, '');
    if (!digits) return '';
    if (digits.charAt(0) === '+') return digits;
    if (digits.slice(0, 2) === '62') return '+' + digits;
    if (digits.charAt(0) === '0') return '+62' + digits.slice(1);
    return '+62' + digits;
  }

  /* The form the dashboard files a reseller under. Deliberately NOT
   * toInternational: that one prefixes +62 to anything it does not recognise,
   * so a foreign number becomes a plausible Indonesian one — fine for a tag the
   * client reads by eye, wrong for the key a record is stored against.
   *
   * Copied verbatim from the agreed contract with hijaberies-dashboard
   * (docs/handoff/2026-08-25-…-agreed.md). Both sides must agree exactly or the
   * same person is filed twice. Do not re-derive it. */
  function toCanonical(raw) {
    var digits = String(raw || '').replace(/[^0-9]/g, '');
    if (!digits) return '';

    var rest = digits;
    while (rest.indexOf('0') === 0 || rest.indexOf('62') === 0) {
      rest = rest.indexOf('62') === 0 ? rest.slice(2) : rest.slice(1);
    }

    /* Every Indonesian mobile starts 8 after the country code. If this one does
       not, hand back the digits as they are — prefixing 62 would mint a
       valid-looking id for the wrong country. */
    return rest.indexOf('8') === 0 ? '62' + rest : digits;
  }

  /* Malformed is refused; foreign is not. A Malaysian number reaches its owner
   * perfectly well — WhatsApp is not a domestic network — so refusing it would
   * turn a contactable reseller away, and would withhold the Shopify customer
   * too, not just the member id.
   *
   * The discriminator is the '+', not the country. "18123456789" is both a
   * plausible US number and the likeliest single-keystroke slip on
   * "08123456789", and nothing in the digits separates them; someone entering a
   * foreign number deliberately writes the '+'. A heuristic, and recorded as
   * one in CR 005.
   *
   * {9,12} is the subscriber part after 62. {8,13} was tried first and let
   * "081234567" through — eight digits after the country code, which no
   * Indonesian mobile has. The ceiling is 12 rather than 11 because the newer
   * 0838 ranges really do run to thirteen digits. */
  function isPlausibleWhatsApp(raw) {
    var s = String(raw || '').trim();
    var digits = s.replace(/[^0-9]/g, '');

    if (s.charAt(0) === '+') return /^[0-9]{8,15}$/.test(digits);
    return /^62[0-9]{9,12}$/.test(toCanonical(s));
  }

  /* Tags are the ONLY channel this form has for anything beyond an email
   * address. {% form 'customer' %} accepts contact[email] and contact[tags] and
   * discards everything else, so the phone number and the qualifying answers
   * are folded in here or they are lost — which is exactly what happened to
   * every registration before CR 003.
   *
   * The hidden field already carries the base tag from Liquid, so a record is
   * never untagged even with this script disabled. */
  function buildTags(form) {
    var field = form.querySelector('[data-rsl-tags]');
    if (!field) return;

    var base = field.getAttribute('data-rsl-base-tag') || field.value || '';
    var tags = base ? [base] : [];

    [
      ['kota', 'kota'],
      ['pengalaman', 'pengalaman'],
      ['kanal', 'kanal'],
      ['wa', 'wa', toInternational],
    ].forEach(function (spec) {
      var el = form.querySelector('[data-rsl-field="' + spec[0] + '"]');
      var raw = el && el.value ? el.value.trim() : '';
      if (!raw) return;
      var value = spec[2] ? spec[2](raw) : raw;
      if (!value) return;
      /* commas separate tags in Shopify, so they cannot appear inside one */
      tags.push(spec[1] + ': ' + value.replace(/,/g, ' '));
    });

    field.value = tags.join(', ');
  }

  /* Crockford's base32: digits and letters, less I, L, O and U. A member number
   * gets printed on a card, read aloud and typed back, so the pairs people
   * confuse doing that — 0/O, 1/I/L — are simply not in the alphabet. */
  var MEMBER_ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';

  /* The id is ours to mint and there is nothing to wait for: no round trip, no
   * read-back, so the number can be shown the moment the form is submitted.
   * Eight characters from a 32-letter alphabet is about 1.1 trillion
   * combinations — a collision is not a practical concern, and the dashboard
   * spots one on promotion and reissues. */
  function generateMemberId(prefix) {
    var bytes = new Uint8Array(8);
    crypto.getRandomValues(bytes);

    var out = '';
    /* 256 divides by 32 exactly, so the modulo spreads evenly. */
    for (var i = 0; i < 8; i += 1) out += MEMBER_ALPHABET[bytes[i] % MEMBER_ALPHABET.length];

    return prefix + out.slice(0, 4) + '-' + out.slice(4);
  }

  /* Fallbacks, not defaults: they are what the page uses when the dashboard
   * cannot be reached. The prefix is stable enough to hardcode — it is printed
   * on physical cards. There is no sensible fallback for the price page, so its
   * absence is shown to the visitor rather than guessed at. */
  var dashboard = { prefix: 'HB-', priceUrl: '' };

  function firestoreSettingUrl(project, doc, key) {
    return (
      'https://firestore.googleapis.com/v1/projects/' +
      encodeURIComponent(project) +
      '/databases/(default)/documents/settings/' +
      encodeURIComponent(doc) +
      '?key=' +
      encodeURIComponent(key)
    );
  }

  /* Read at page load and kept in memory, rather than at submit time. Same
   * property the dashboard asked for — nothing is baked into the theme, and
   * changing their field moves us without a deploy — but it keeps a network
   * round trip off the path between the visitor pressing submit and the page
   * navigating away. */
  function loadDashboardSettings() {
    var root = document.querySelector('[data-rsl-project]');
    if (!root || typeof fetch !== 'function') return;

    var project = root.getAttribute('data-rsl-project');
    var key = root.getAttribute('data-rsl-api-key');
    if (!project || !key) return;

    var read = function (doc, apply) {
      fetch(firestoreSettingUrl(project, doc, key))
        .then(function (r) {
          return r.ok ? r.json() : null;
        })
        .then(function (body) {
          if (body && body.fields) apply(body.fields);
        })
        .catch(function () {
          /* Offline, blocked, or the project moved. The fallbacks stand and the
             visitor still gets a member number. */
        });
    };

    read('membership', function (f) {
      var v = f.numberPrefix && f.numberPrefix.stringValue;
      if (v) dashboard.prefix = v;
    });
    read('brand', function (f) {
      var v = f.pricePageUrl && f.pricePageUrl.stringValue;
      if (v) dashboard.priceUrl = v;
    });
  }

  /* An empty string means "no link", which the panel says out loud. It must
     never return a half-built URL — a bare origin, or one ending in a slash,
     reads as a working link and is not one. */
  function memberLinkFor(id) {
    if (!dashboard.priceUrl || !id) return '';
    return dashboard.priceUrl.replace(/\/+$/, '') + '/' + id;
  }

  function fieldValue(form, name) {
    var el = form.querySelector('[data-rsl-field="' + name + '"]');
    return el && el.value ? el.value.trim() : '';
  }

  /* The visible text AND the block id behind it. The two dropdowns are
   * theme-editor blocks the client can reword, reorder and replace at will, so
   * the text is what a human reads in the dashboard and the key is what
   * survives a rename. Without the key, rewording one option quietly splits a
   * cohort in two months after anyone remembers why. */
  function selectedOption(form, name) {
    var el = form.querySelector('[data-rsl-field="' + name + '"]');
    if (!el || !el.options || el.selectedIndex < 0) return { text: '', key: '' };
    var opt = el.options[el.selectedIndex];
    return {
      text: el.value || '',
      key: (opt && opt.getAttribute('data-rsl-key')) || '',
    };
  }

  /* Firestore's REST shape tags every value with its type. All twelve fields
     in the agreed contract are strings. */
  function firestoreString(value) {
    return { stringValue: value == null ? '' : String(value) };
  }

  /* Fire-and-forget, after the Shopify POST and never gating it.
   *
   * A failed write shows the visitor nothing: they still have a customer
   * account and the dashboard can reconcile, whereas a customer who could not
   * register is unrecoverable. That silence is why the payload is asserted
   * key-by-key in the tests — nothing downstream reports a document this page
   * gets wrong. */
  function sendRegistration(form, memberId) {
    var root = document.querySelector('[data-rsl-project]');
    if (!root || typeof fetch !== 'function') return;

    var project = root.getAttribute('data-rsl-project');
    var key = root.getAttribute('data-rsl-api-key');
    if (!project || !key) return;

    /* Something filled a field no person can see. Drop the write and say
       nothing — the registration itself still goes through, so a false positive
       costs a lead its dashboard record and never its account. */
    var hp = form.querySelector('[data-rsl-hp]');
    if (hp && hp.value) return;

    var typed = fieldValue(form, 'wa');
    var experience = selectedOption(form, 'pengalaman');
    var channel = selectedOption(form, 'kanal');
    var email = form.querySelector('[name="contact[email]"]');

    /* Exactly these twelve keys. The dashboard's rules use hasOnly(), so one
       unknown key fails the whole write — silently, like every other failure
       here. */
    var doc = {
      fields: {
        memberId: firestoreString(memberId),
        name: firestoreString(fieldValue(form, 'nama')),
        email: firestoreString(email ? email.value.trim() : ''),
        whatsApp: firestoreString(toCanonical(typed)),
        whatsAppTyped: firestoreString(typed),
        city: firestoreString(fieldValue(form, 'kota')),
        experience: firestoreString(experience.text),
        experienceKey: firestoreString(experience.key),
        channel: firestoreString(channel.text),
        channelKey: firestoreString(channel.key),
        submittedAt: firestoreString(new Date().toISOString()),
        source: firestoreString('shopify-register'),
      },
    };

    var url =
      'https://firestore.googleapis.com/v1/projects/' +
      encodeURIComponent(project) +
      '/databases/(default)/documents/registrations?key=' +
      encodeURIComponent(key);

    /* keepalive rather than navigator.sendBeacon: a beacon cannot send
       application/json, because that content type needs a CORS preflight the
       beacon API is not allowed to make, and Firestore's REST endpoint requires
       it. A plain fetch would be cancelled the moment the form navigates. */
    try {
      fetch(url, {
        method: 'POST',
        keepalive: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(doc),
      }).catch(function () {
        /* Silent by design — see above. */
      });
    } catch (e) {
      /* Older browsers without keepalive. The customer is unaffected. */
    }
  }

  /* Shown in the browser's own validation bubble when the section's message
     setting has been emptied. The bubble needs some text or the field counts as
     valid, so this cannot be blank — but the on-page note stays absent, which is
     what playbook rule 8 is about. */
  var WA_FALLBACK_MESSAGE = 'Nomor WhatsApp tidak valid.';

  /* Marks the field valid or invalid through constraint validation, and returns
     whether it may proceed.
     *
     * Constraint validation, NOT preventDefault, is what actually stops this.
     * Shopify's spam protection registers its own submit listener before ours;
     * on a protected store it prevents the default, runs the captcha challenge,
     * and then calls form.submit() — which dispatches no event and so cannot be
     * prevented by any listener. A version of this that only called
     * preventDefault showed the message and posted the registration anyway, on
     * the live store, creating a customer record from a number nobody can dial.
     *
     * setCustomValidity stops the browser before the submit event is dispatched
     * at all, so the captcha flow never begins. It is the same mechanism that
     * makes `required` unbypassable. */
  function syncWhatsAppValidity(form) {
    var field = form.querySelector('[data-rsl-field="wa"]');
    if (!field || typeof field.setCustomValidity !== 'function') return true;

    var note = document.querySelector('[data-rsl-wa-error]');
    var typed = field.value.trim();

    /* An empty field belongs to `required`, whose message — "please fill this
       in" — is the right one; "check the number" is not. So emptiness is
       explicitly not ours: we judge a number only once there is one. */
    var ok = !typed || isPlausibleWhatsApp(typed);

    field.setCustomValidity(
      ok ? '' : (note && note.textContent.trim()) || WA_FALLBACK_MESSAGE
    );
    if (note) note.hidden = ok;

    return ok;
  }

  function stashOnSubmit() {
    var form = document.getElementById('rsl-register-form');
    if (!form) return;

    /* Keep the constraint in step with what is being typed. Without this the
       field stays invalid after the first bad attempt and could never be
       corrected — the browser would go on refusing a number the visitor has
       already fixed. */
    var waField = form.querySelector('[data-rsl-field="wa"]');
    if (waField) {
      waField.addEventListener('input', function () {
        syncWhatsAppValidity(form);
      });
    }

    form.addEventListener('submit', function (event) {
      var read = function (name) {
        var el = form.querySelector('[data-rsl-field="' + name + '"]');
        return el ? el.value.trim() : '';
      };

      /* Belt and braces. Constraint validation should mean this event never
         fires with a bad number — the browser refuses first. It is kept for the
         paths that skip validation: a synthetic dispatchEvent, and any script
         calling requestSubmit on a form we have not re-synced. */
      if (!syncWhatsAppValidity(form)) {
        event.preventDefault();
        return;
      }

      /* Normalisation happens inside buildTags, against the value the visitor
         typed. The visible input is deliberately left alone — rewriting it
         while the page is navigating away just makes the field flicker. */
      buildTags(form);

      /* Minted here, not asked for. Nothing is awaited, so the number exists
         before the form has finished leaving the page. */
      var memberId = generateMemberId(dashboard.prefix);

      try {
        sessionStorage.setItem(
          STASH_KEY,
          JSON.stringify({
            nama: read('nama'),
            kota: read('kota'),
            memberId: memberId,
            memberLink: memberLinkFor(memberId),
          })
        );
      } catch (e) {
        /* Private browsing, storage disabled. The generic message still works,
           but the panel cannot render and the reseller will not see their
           number — a degradation recorded on cr005-002, recoverable because
           the dashboard holds the id against their record. */
      }

      /* Deliberately outside that try: the dashboard write must not depend on
         sessionStorage having worked. The case where the visitor cannot see
         their own number is exactly the case where the dashboard holding it
         matters most. */
      sendRegistration(form, memberId);
    });
  }

  /* Read once and cleared once. Two things now consume the stash — the panel
     and the WhatsApp message — and whichever ran first used to delete it out
     from under the other. */
  var stashTaken = false;
  var stashValue = null;

  function takeStash() {
    if (stashTaken) return stashValue;
    stashTaken = true;
    try {
      stashValue = JSON.parse(sessionStorage.getItem(STASH_KEY) || 'null');
      sessionStorage.removeItem(STASH_KEY);
    } catch (e) {
      stashValue = null;
    }
    return stashValue;
  }

  /* The guarantee, as opposed to the WhatsApp message, which is best-effort.
     This function deliberately reads none of the message settings: the client
     can edit that message and drop its placeholders, and the reseller must
     still leave with their number. */
  function renderMemberPanel() {
    var panel = document.querySelector('[data-rsl-member]');
    if (!panel) return;

    var data = takeStash();
    if (!data || !data.memberId) return; /* stays hidden — never an empty panel */

    var idEl = panel.querySelector('[data-rsl-member-id]');
    if (idEl) idEl.textContent = data.memberId;

    var linkEl = panel.querySelector('[data-rsl-member-link]');
    var noLink = panel.querySelector('[data-rsl-member-nolink]');

    if (data.memberLink) {
      if (linkEl) {
        linkEl.setAttribute('href', data.memberLink);
        linkEl.textContent = data.memberLink;
        linkEl.hidden = false;
      }
      if (noLink) noLink.hidden = true;
    } else {
      /* The number is ours and always shown; only the link is missing, and the
         panel says so rather than rendering something that looks clickable. */
      if (linkEl) {
        linkEl.removeAttribute('href');
        linkEl.hidden = true;
      }
      if (noLink) noLink.hidden = false;
    }

    panel.hidden = false;
  }

  function personaliseWhatsAppLink() {
    var link = document.querySelector('[data-rsl-wa-personalised]');
    if (!link) return;

    var data = takeStash();
    if (!data || (!data.nama && !data.kota)) return;

    var template = link.getAttribute('data-rsl-template');
    if (!template) return;

    /* {nomor} and {link} are best-effort by construction. This message is a
       merchant-editable theme setting, so the client can remove either at any
       time — which is exactly why the panel above does not depend on it. */
    var fill = function (text) {
      return text
        .replace('{nama}', data.nama || '')
        .replace('{kota}', data.kota || '')
        .replace('{nomor}', data.memberId || '')
        .replace('{link}', data.memberLink || '');
    };

    var message = fill(template).replace(/[^\S\n]{2,}/g, ' ').trim();

    /* The number and the link are the two things the reseller needs to keep, and
       the merchant's saved message predates both placeholders — every store
       upgrading to this has a message naming neither. Appending them when the
       template does not mention them means the reseller gets them in their own
       chat history without anyone having to edit a theme setting first. Using
       the placeholders explicitly still wins, so placement stays controllable. */
    var appendLine = link.getAttribute('data-rsl-append');
    var names = template.indexOf('{nomor}') !== -1 || template.indexOf('{link}') !== -1;

    if (appendLine && !names && (data.memberId || data.memberLink)) {
      var extra = fill(appendLine).replace(/[^\S\n]{2,}/g, ' ').trim();
      if (extra) message = message ? message + '\n' + extra : extra;
    }

    var href = link.getAttribute('href') || '';
    var base = href.split('?')[0];
    if (!base || base === '#') return;

    link.setAttribute('href', base + '?text=' + encodeURIComponent(message));
  }

  function init() {
    reserveSpaceForStickyCta();
    /* Started early and never awaited: by the time anyone submits, the prefix
       and price-page URL are usually in hand, and if they are not the
       fallbacks cover it. */
    loadDashboardSettings();
    stashOnSubmit();
    renderMemberPanel();
    personaliseWhatsAppLink();
    Array.prototype.forEach.call(
      document.querySelectorAll('[data-rsl-calculator]'),
      setUpCalculator
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  /* The theme editor swaps section markup in and out without a page reload. */
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', init);
})();
